import React, { useEffect, useState } from "react";
import { useAppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { MembershipAPI } from "../../api/membership";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { ReferralsAPI } from "../../api/referral";
import { GridCloseIcon } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import {
  memberOptionGroup,
  memberOptionLabel,
  sortMembersDepartedLast,
} from "../helper";
import renderMemberOption from "../MemberOption";

const AddReferralModal = ({ open, handleClose, setIsLoading }) => {
  const { openSnack } = useAppContext();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);

  const [formState, setFormState] = useState({
    referral_name: "",
    referral_date: "",
    referral_sender_id: "",
    referral_receiver_id: "",
  });
  const [formError, setFormError] = useState({});

  const handleError = (formObj = formState) => {
    const temp = { ...formError };

    if ("referral_name" in formObj) {
      temp.referral_name = formObj.referral_name
        ? ""
        : "Referral name is required.";
    }

    if ("referral_date" in formObj) {
      temp.referral_date = formObj.referral_date
        ? ""
        : "Referral date is required.";
    }

    if ("referral_sender_id" in formObj) {
      temp.referral_sender_id = formObj.referral_sender_id
        ? ""
        : "Referral sender is required.";
    }

    if ("referral_receiver_id" in formObj) {
      temp.referral_receiver_id = formObj.referral_receiver_id
        ? ""
        : "Referral receiver is required.";
    }

    setFormError({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleChange = (key, value) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
    handleError({ [key]: value });
  };

  const fetchMembers = async () => {
    try {
      const response = await MembershipAPI.getMembers("");
      setMembers(response?.items || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderForm = () => {
    return (
      <Grid
        container
        spacing={2}
        rowSpacing={2.5}
        sx={{
          padding: "16px 0",
        }}
      >
        <Grid item xs={12} sm={6}>
          <Autocomplete
            value={formState?.referral_sender_id || null}
            onChange={(_e, newValue) =>
              handleChange("referral_sender_id", newValue)
            }
            id="multiple-limit-tags"
            options={sortMembersDepartedLast(members)}
            groupBy={memberOptionGroup}
            getOptionLabel={memberOptionLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={renderMemberOption}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Referral Sender*"
                className="login-textField"
                size="small"
                error={!!formError.referral_sender_id}
                helperText={formError.referral_sender_id}
              />
            )}
            className="login-textField"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            value={formState?.referral_receiver_id || null}
            onChange={(_e, newValue) =>
              handleChange("referral_receiver_id", newValue)
            }
            id="multiple-limit-tags"
            options={sortMembersDepartedLast(members)}
            groupBy={memberOptionGroup}
            getOptionLabel={memberOptionLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={renderMemberOption}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Referral Receiver*"
                className="login-textField"
                size="small"
                error={!!formError.referral_receiver_id}
                helperText={formError.referral_receiver_id}
              />
            )}
            className="login-textField"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          {" "}
          <TextField
            margin="normal"
            fullWidth
            id="referral_name"
            label="Referral Name*"
            name="referral_name"
            value={formState?.referral_name}
            onChange={(e) => handleChange("referral_name", e.target.value)}
            className="login-textField "
            size="small"
            error={!!formError.referral_name}
            helperText={formError.referral_name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Referral Date*"
            value={formState?.referral_date || null}
            onChange={(newValue) => handleChange("referral_date", newValue)}
            className="login-textField date-textField"
            sx={{ width: "100%", height: "40px" }}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "referral_date",
                name: "referral_date",
                className: "login-textField",
                size: "small",
                error: !!formError.referral_date,
                helperText: formError.referral_date,
              },
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const AddReferral = async (form) => {
    const formObj = {
      ...form,
      referral_date: dayjs(form.referral_date).format("YYYY-MM-DD"),
      referral_sender_id: form.referral_sender_id?.id,
      referral_receiver_id: form.referral_receiver_id?.id,
    };
    try {
      await ReferralsAPI.addReferral(formObj);
      openSnack("Referral created successfully.", "success");
      setIsLoading(true);
      handleClose();
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  const handleSubmit = () => {
    if (!handleError()) return;
    if (
      formState?.referral_receiver_id?.id === formState?.referral_sender_id?.id
    ) {
      openSnack(
        "Referral Sender and Referral Receiver can not be the same person.",
        "error",
      );
      return;
    }
    AddReferral(formState);
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) =>
        reason === "backdropClick" ? "" : handleClose()
      }
      hideBackdrop
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="custom-modal"
      sx={{
        ".MuiPaper-root": {
          width: "834px",
        },
      }}
    >
      <div className="modal-header">
        <h2 className="header-text">Create New Referral</h2>
        <IconButton onClick={() => handleClose()}>
          <GridCloseIcon />
        </IconButton>
      </div>
      {renderForm()}

      <DialogActions
        sx={{ paddingTop: `${formError?.dues_paid_date ? "24px" : "8px"}` }}
        className="modal-btn-container"
      >
        <Button onClick={handleClose} className="button-common light-blue-bg">
          Cancel
        </Button>
        <Button onClick={handleSubmit} className="add-new-button button-common">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddReferralModal;
